import Tone from 'tone';
import MidiCCParameter from 'parameters/MidiCCParameter.js';
import MutableTrack from 'track/MutableTrack.js';
import {DEVICE_TO_CONFIG} from 'constants/midi-cc-interfaces.js';
import MidiInterfaceEffect from 'effects/midi/MidiInterfaceEffect.js';
import {getMilliseconds} from './getMilliseconds.js';
import recorder from '../recorder.js';
import SoundUploader from 'api/SoundUploader.js';
import Sampler from 'instruments/Sampler.js';
import RecordedTrigger from 'keyboard/RecordedTrigger.js';
import Notes from 'midi/Notes.js';
import Resolution from 'engine/Resolution.js';
import ipfs from 'decentralized/Ipfs.js';
import RecordableTrack from './RecordableTrack.js';
import {MIDI} from 'constants/track-types.js';
import {EXTERNAL_INSTRUMENT} from 'constants/instrument-types.js';
import {ALL, COMPUTER_KEYBOARD} from 'constants/midi-input-types.js';

const TIME_16N = new Tone.Time('16n');

// the delay at which we actually start recording, for some reason
// we record too early 
const DELAY = 0;
const OVERLAP = 10;

class MidiTrack extends RecordableTrack {
    constructor(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence, dontConnectEffects) {
        super(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence);
        this.midiOutputs = new Set();
        this.midiEngine = undefined;

        this.isRecordingMidi = false;
        this.isEnabled = false;
        this.recordedTriggers = {};

        if (dontConnectEffects == undefined || !dontConnectEffects) {
            this.setupEffects();
        }

        this.audioInputManager = undefined;

        // the midi channel (i.e. 'all' or a number)
        this.midiChannel = undefined;

        this.gated = true;
        this.latency = 0;
        this.ccParameters = [];
        
    }

    setupCCParameters(output) {
        console.log("OUTPUT =", output);
        this.ccParameters = [];
        for (let i=1; i < 120; i++) {
            let ccParameter = new MidiCCParameter(
                'Midi CC ' + i,
                i,
                output
            );

            // dont add the ccParameter to the list of parameters but
            // register it
            this.registerParameter(ccParameter, false);

            // add it to this special list
            this.ccParameters.push(ccParameter);
        }
    }

    getParameters() {
        return [
            ... super.getParameters(),
            ... this.ccParameters
        ];
    }

    getInstrumentType() {
        return EXTERNAL_INSTRUMENT;
    }

    getName() {
        return "External Instrument";
    }

    getCopy() {
        let track = super.getCopy((a,b,c,d,e,f) => new MidiTrack(a,b,c,d,e,f));
        track.setMidiEngine(this.midiEngine);
        track.midiChannel = this.middieChannel;
        track.sequencerEngine = this.sequencerEngine;
        track.midiOutputs = new Set(this.midiOutputs);
        track.latency = this.latency;
        return track;
    }

    getSoundUploader() {
        return this.soundUploader;
    }

    setAudioSettings(audioSettings) {
        this.audioSettings = audioSettings;
    }

    setSequencerEngine(sequencerEngine) {
        this.sequencerEngine = sequencerEngine;
    }

    recording(r) {
        this.isRecordingMidi = r;
    }

    toggleRecording(number) {
        this.pattern.loopsPlaying[number] = !this.pattern.loopsPlaying[number];
    }

    isRecordingPlaying(number) {
        return this.pattern.loopsPlaying[number];
    }

    getRecordings() {
        return this.getSamplers();
    }

    deleteMidiOutput(output) {
        if (this.midiOutputs.has(output)) {
            this.midiOutputs.delete(output);
        }
    }

    addMidiOutput(output, skip=false) {
        /*
        if (output.name in DEVICE_TO_CONFIG && !skip) {
            this.addMidiInterfaceEffect(output, DEVICE_TO_CONFIG[output.name]);
        }
        */
        this.midiOutputs.add(output);
        this.setupCCParameters(this.sequencerEngine.midi.deviceToMidiCC[output.name]);
    }

    addMidiInterfaceEffect(output, config) {
        // first check if theres already a midi interface
        let midiInterface = this.midiEffects.effects.filter(
            x => x.name === 'Midi Interface')[0];
        if (midiInterface) {
            return;
        }
        let effect = new MidiInterfaceEffect(this);
        effect.setConfig(output, config);
        this.midiEffects.addEffect(effect);

        // todo: force render update on just the effects chain via redux
        this.sequencerEngine.update();
    }


    setAudioInputManager(audioInputManager) {
        this.audioInputManager = audioInputManager;
    }

    onLoadTrackUrl(url, soundId, samplerData) {
        let sampler = super.onLoadTrackUrl(url, soundId, samplerData);
        return sampler;
    }

    disconnect() {
        if (this.audioChannel !== undefined) {
           this.audioInputManager.disconnect(this.gainNode, this.audioChannel);
        }
        this.audioChannel = undefined;
    }

    getMidiOutputs() {
        return this.midiOutputs;
    }

    setMidiEngine(engine) {
        this.midiEngine = engine;
        let self = this;
        setTimeout(function() {
            self.setupMidiInput();
        }, 1000);
    }

    inputNoteForStep(step, note) {
        let transpose = Notes.fromString(note).getTranspose();
        let steps = this.getStepsAtResolution(this.sequencerEngine.resolution);
        steps[step].transpose = transpose;
        this.syncBuckets(this.sequencerEngine.resolution);
        this.update();
    }

    triggerMidiNoteOn(transpose, velocity=1) {
        // convert tranpose to note
        this.onNoteOn(undefined, Notes.fromTranspose(transpose).toString(), velocity);
    }

    onNoteOn(name, message, velocity=1) {
        if (this.sequencerEngine.cursor.step !== undefined &&
            this.sequencerEngine.cursor.show) {
            // Let user input the note for a step via a keyboard interface
            this.inputNoteForStep(
                this.sequencerEngine.cursor.getStep(
                    this.sequencerEngine.resolution), message);
        }

        // only proceed if the track is "enabled" i.e. the red recording box is clicked
        if (!this.isEnabled) {
            return;
        }

        // if it's the midi input of the track (todo), then
        // we send a note on response to whatever is tracked on
        // output
        if (this.midiOutputs.size > 0) {
            this.midiOutputs.forEach(
                output =>
                    this.midiEngine.playNoteSimple(output, message, this.midiChannel, 0, 0, velocity));
        }

        if (this.isRecordingMidi)  {
            /*
            this.recordedTriggers[message] = new RecordedTrigger(
                undefined,
                0,
                this.sequencerEngine.resolution,
                this.sequencerEngine, true);
                */
        }
    }

    triggerMidiNoteOff(transpose) {
        // convert tranpose to note
        this.onNoteOff(undefined, Notes.fromTranspose(transpose).toString());
    }

    onNoteOff(name, message) {
        // if it's the midi input of the track (todo), then
        // we send a note on response to whatever is tracked on
        // output
        if (this.midiOutputs.size > 0) {
            this.midiOutputs.forEach(
                output => 
                    this.midiEngine.releaseNoteSimple(output, message, this.midiChannel, 0));
        }

        
        if (this.isRecordingMidi && message in this.recordedTriggers) {
            /*
            let transpose = Notes.fromString(message).getTranspose();
            this.recordedTriggers[message].record(this.sequencerEngine, this, transpose);
            */
        }
    }

    setupMidiInput() {
        let self = this;
   }

    getTrackType() {
        return MIDI;
    }

    liveTrigger(stepData) {
        this.triggerMidiNoteOn(stepData.transpose);
    }

    liveTriggerRelease(transpose) {
        this.triggerMidiNoteOff(transpose);
    }

    // function for triggering a midi note
    async trigger(stepData, time) {
        if (this.isFrozen) {
            super.trigger(stepData, time);
            return;
        }
        time -= this.latency / 1000;
        let transpose = stepData.transpose;
        let duration = new Tone.Time(new Tone.Time(stepData.duration) * (TIME_16N));
        let timeMilliseconds = getMilliseconds(time);
        let release = stepData.release / 16.0;
        let chops = stepData.chop;
        let chopDur = duration == null ? TIME_16N : duration;

        let chopRate = chopDur / chops;
        let chopTime = time;
        let velocity = stepData.velocity;
        velocity *= (1 - stepData.start);

        for (let i=0; i < chops; i++) {
            this.midiOutputs.forEach(
                output => 
                    this.midiEngine.playNote(
                        output,
                        this.midiChannel,
                        transpose,
                        getMilliseconds(chopTime), duration.toMilliseconds(),
                        release,
                        velocity));
            chopTime = chopTime + chopRate;
        }

    }

    expandRecordedTracks() {
        let loopsPlaying = Object.keys(this.pattern.loopsPlaying)
            .filter(key => this.pattern.loopsPlaying[key]);

        console.log('loops playing = ');
        console.log(loopsPlaying);
        console.log(this.samplers);
        loopsPlaying.forEach(id =>
            this.sequencerEngine.addSound(id, undefined, undefined, undefined, true));
    }

    getJson() {
        return {
            ... super.getJson(),
            latency: this.latency,
        };
    }

    fromJson(json) {
        if (json.latency) {
            this.latency = json.latency;
        }
    }
}


export default MidiTrack;
